<script setup lang="ts">
import { ref, computed, watch, nextTick, useTemplateRef } from 'vue';
import { z } from 'zod';
import {
  UiDialog, UiForm, UiFormField, UiInputField, UiSelect, UiInfoBox, UiText,
} from '@shopexperts/shared';
import type { AdminExpertClaim, AdminUpdateExpertClaimRequest } from '@shopexperts/legacy-api';
import { shopifyRoleOptions } from '@/utils/roles';
import { useExpertClaimActions } from '@/app/composables/expert-claims/useExpertClaimActions';
import { extractStatus, mapValidationErrors } from '@/app/composables/claim/claimApiErrors';
import { isLinkedInUrl } from '@/app/composables/claim/linkedinUrl';

const props = defineProps<{
  claim: AdminExpertClaim | null;
}>();

const model = defineModel<boolean>({ default: false });

const { update } = useExpertClaimActions();

const isSaving = ref(false);
const serverErrors = ref<Record<string, string | undefined> | undefined>(undefined);
const formRef = useTemplateRef<{ resetForm: () => void; forceClear: () => void }>('formRef');
const submitRef = useTemplateRef<HTMLButtonElement>('submitRef');

// Mirrors the API's LinkedinUrl rule (strict full-URL semantics: the admin
// edits the stored value directly, so no scheme-prepending leniency here).
const linkedinUrlSchema = z.string()
  .min(1, 'LinkedIn URL is required')
  .max(255, 'LinkedIn URL must be 255 characters or less')
  .refine(isLinkedInUrl, 'Enter a full linkedin.com URL (https://...)');

const freelancerSchema = z.object({
  first_name: z.string().min(1, 'First name is required').max(100, 'First name must be 100 characters or less'),
  last_name: z.string().min(1, 'Last name is required').max(100, 'Last name must be 100 characters or less'),
  role: z.string().min(1, 'Role is required').max(100, 'Role must be 100 characters or less'),
  email: z.email('Enter a valid email address').max(255, 'Email must be 255 characters or less'),
  linkedin_url: linkedinUrlSchema,
});

const agencySchema = z.object({
  full_name: z.string().min(1, 'Full name is required').max(200, 'Full name must be 200 characters or less'),
  company_name: z.string().min(1, 'Company name is required').max(200, 'Company name must be 200 characters or less'),
  company_email: z.email('Enter a valid email address').max(255, 'Company email must be 255 characters or less'),
  email: z.email('Enter a valid email address').max(255, 'Email must be 255 characters or less'),
  linkedin_url: linkedinUrlSchema,
});

const schema = computed(() => (props.claim?.type === 'agency' ? agencySchema : freelancerSchema));

// The role dropdown is a frontend concern (the API accepts any string ≤100),
// so keep an out-of-list stored value selectable.
const roleOptions = computed(() => {
  const current = props.claim?.role;
  if (current && !shopifyRoleOptions.some((option) => option.value === current)) {
    return [{ value: current, label: current }, ...shopifyRoleOptions];
  }
  return shopifyRoleOptions;
});

const editableFields = computed<readonly string[]>(() => {
  if (!props.claim) return [];
  return props.claim.type === 'freelancer'
    ? ['first_name', 'last_name', 'role', 'email', 'linkedin_url']
    : ['full_name', 'company_name', 'company_email', 'email', 'linkedin_url'];
});

const initialValues = computed<Record<string, string>>(() => {
  const claim = props.claim;
  if (!claim) return {};
  const values: Record<string, string> = {};
  for (const field of editableFields.value) {
    values[field] = (claim[field as keyof AdminExpertClaim] as string | null) ?? '';
  }
  return values;
});

// Remounts whenever the claim changes server-side, so the form always starts
// from the latest values; reopening on an unchanged claim resets below.
const formKey = computed(() => (props.claim ? `${props.claim.id}-${props.claim.updated_at}` : 'none'));

watch(model, async (isOpen) => {
  if (isOpen && props.claim) {
    serverErrors.value = undefined;
    await nextTick();
    formRef.value?.resetForm();
    formRef.value?.forceClear();
  }
});

// The API resets verification only when the email changes case-insensitively.
function isEmailChanged(value: unknown): boolean {
  if (!props.claim) return false;
  const next = String(value ?? '').trim().toLowerCase();
  return !!next && next !== props.claim.email.toLowerCase();
}

/** Only changed fields — the API validates with `sometimes`. */
function buildChanges(values: Record<string, unknown>): AdminUpdateExpertClaimRequest {
  const claim = props.claim;
  if (!claim) return {};
  const result: Record<string, string> = {};
  for (const field of editableFields.value) {
    const original = (claim[field as keyof AdminExpertClaim] as string | null) ?? '';
    const next = String(values[field] ?? '').trim();
    if (next !== original) result[field] = next;
  }
  return result as AdminUpdateExpertClaimRequest;
}

function triggerSubmit() {
  submitRef.value?.click();
}

// Mirrors EmbeddedClaimListing: editing any field clears stale 422 messages,
// otherwise UiForm's re-apply watcher keeps pinning them to corrected values.
function handleFormValuesChange() {
  if (serverErrors.value) serverErrors.value = undefined;
}

async function onSubmit(values: Record<string, unknown>) {
  if (!props.claim || isSaving.value) return;

  const changes = buildChanges(values);
  if (Object.keys(changes).length === 0) {
    model.value = false;
    return;
  }

  isSaving.value = true;
  serverErrors.value = undefined;
  try {
    await update.mutateAsync({
      claimId: props.claim.id,
      changes,
      // A casing-only email edit is still sent (the stored value updates) but
      // the API resets verification only on a case-insensitive change — gate
      // the success toast's copy on the same rule as the warning banner.
      emailChanged: changes.email !== undefined && isEmailChanged(changes.email),
    });
    model.value = false;
  } catch (error) {
    if (extractStatus(error) === 422) {
      serverErrors.value = mapValidationErrors(error);
    } else {
      // Toasted by the actions composable (e.g. 409 "A decided claim cannot be
      // edited. Cancel the decision first."). The list refetch will disable
      // Edit, so close the stale form.
      model.value = false;
    }
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <UiDialog
    v-model="model"
    title="Edit claim"
    size="lg"
    :loading="isSaving"
    primary-action-label="Save changes"
    secondary-action-label="Cancel"
    @primary-action="triggerSubmit"
    @secondary-action="model = false"
  >
    <UiForm
      v-if="claim"
      :key="formKey"
      ref="formRef"
      :schema="schema"
      :initial-values="(initialValues as any)"
      :server-errors="serverErrors"
      @update:model-value="handleFormValuesChange"
      @submit="onSubmit"
    >
      <button ref="submitRef" type="submit" class="hidden" />

      <div class="flex flex-col gap-5">
        <UiText
          as="p"
          variant="caption-normal"
          class="text-gray-400"
          :text="`Claim #${claim.id} · ${claim.type === 'agency' ? 'Agency' : 'Freelancer'} claim of ${claim.expert.name || claim.expert.slug}`"
        />

        <template v-if="claim.type === 'freelancer'">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <UiFormField name="first_name" label="First name">
              <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                <UiInputField
                  :id="id"
                  name="first_name"
                  :model-value="modelValue as string"
                  :invalid="invalid"
                  :error="error"
                  :disabled="isSaving"
                  placeholder="First name"
                  @update:model-value="onUpdateModelValue"
                  @validate="onValidate"
                />
              </template>
            </UiFormField>

            <UiFormField name="last_name" label="Last name">
              <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                <UiInputField
                  :id="id"
                  name="last_name"
                  :model-value="modelValue as string"
                  :invalid="invalid"
                  :error="error"
                  :disabled="isSaving"
                  placeholder="Last name"
                  @update:model-value="onUpdateModelValue"
                  @validate="onValidate"
                />
              </template>
            </UiFormField>
          </div>

          <UiFormField name="role" label="Role">
            <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
              <UiSelect
                :id="id"
                name="role"
                :options="roleOptions"
                :model-value="modelValue as string"
                :invalid="invalid"
                :error="error"
                :disabled="isSaving"
                placeholder="Select a role"
                @update:model-value="onUpdateModelValue"
                @validate="onValidate"
              />
            </template>
          </UiFormField>
        </template>

        <template v-else>
          <UiFormField name="full_name" label="Full name">
            <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
              <UiInputField
                :id="id"
                name="full_name"
                :model-value="modelValue as string"
                :invalid="invalid"
                :error="error"
                :disabled="isSaving"
                placeholder="Full name"
                @update:model-value="onUpdateModelValue"
                @validate="onValidate"
              />
            </template>
          </UiFormField>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <UiFormField name="company_name" label="Company name">
              <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                <UiInputField
                  :id="id"
                  name="company_name"
                  :model-value="modelValue as string"
                  :invalid="invalid"
                  :error="error"
                  :disabled="isSaving"
                  placeholder="Company name"
                  @update:model-value="onUpdateModelValue"
                  @validate="onValidate"
                />
              </template>
            </UiFormField>

            <UiFormField name="company_email" label="Company email">
              <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                <UiInputField
                  :id="id"
                  name="company_email"
                  type="email"
                  :model-value="modelValue as string"
                  :invalid="invalid"
                  :error="error"
                  :disabled="isSaving"
                  placeholder="info@company.com"
                  @update:model-value="onUpdateModelValue"
                  @validate="onValidate"
                />
              </template>
            </UiFormField>
          </div>
        </template>

        <UiFormField name="email" label="Claimant email">
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
            <div class="flex flex-col gap-2">
              <UiInputField
                :id="id"
                name="email"
                type="email"
                :model-value="modelValue as string"
                :invalid="invalid"
                :error="error"
                :disabled="isSaving"
                placeholder="claimant@example.com"
                @update:model-value="onUpdateModelValue"
                @validate="onValidate"
              />
              <UiInfoBox v-if="isEmailChanged(modelValue)" variant="warning">
                Changing the email resets the claim to unverified and re-sends the verification email to the new address.
              </UiInfoBox>
            </div>
          </template>
        </UiFormField>

        <UiFormField name="linkedin_url" label="LinkedIn URL">
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
            <UiInputField
              :id="id"
              name="linkedin_url"
              :model-value="modelValue as string"
              :invalid="invalid"
              :error="error"
              :disabled="isSaving"
              placeholder="https://www.linkedin.com/in/..."
              @update:model-value="onUpdateModelValue"
              @validate="onValidate"
            />
          </template>
        </UiFormField>
      </div>
    </UiForm>
  </UiDialog>
</template>
